import { Box, Divider, Skeleton, Stack, Tab, Tabs, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import './consommation.scss';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { ConfigConsommationsDto } from '../../../services/dtos/config-consommations.dto';
import { mappingTypConso } from '../../../utils/traitment.utils';
import theme from '../../../theme';
import { getConsommationsTeleverse } from '../../../services/consommations.service';
import { showAxiosMessageError } from '../../../services/error-message.service';
import { BarChart } from '@mui/x-charts/BarChart';
import { ConsommationTelereleveDto } from '../../../services/dtos/consommation-telereleve.dto';
import { AxisValueFormatterContext } from '@mui/x-charts/models/axis';
import DatePicker, { DateObject } from 'react-multi-date-picker';
import { DateRangeDto } from '../../../services/dtos/date-range.dto';
import { DAYS, MONTHS, YEARS } from '../../../utils/constants.utils';
import { ConfigGraphConsommationsDto } from '../../../services/dtos/config-graph-consommations.dto';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import gregorianFr from '../../../utils/date.utils';

/**
 * Composant pour les tableau et graphiques des données de consommation.
 * @param props
 * @constructor
 */
export default function ConsommationTelereleve(props: {
    contractId: string | undefined;
    config: ConfigConsommationsDto;
}) {
    // Snackbar des messages
    const showMessage = useSnackbars();
    // Recherche des données
    const [isSearching, setIsSearching] = useState<boolean>(false);
    // Données pour le graph
    const [datas, setDatas] = useState<Array<ConsommationTelereleveDto>>();
    const [dataY, setDataY] = useState<Array<number>>([]);
    const [dataX, setDataX] = useState<Array<string>>();
    // Type de période
    const [type, setType] = React.useState<string>(props.config?.configurationGraphique[0].type);
    // Données pour le date picker
    const [dateRange, setDateRange] = React.useState<DateRangeDto>();

    useEffect(() => {
        // A chaque modification du type de période on change les values utiles au datePicker
        const currentConfig = props.config.configurationGraphique.find(obj => obj.type === type);
        if (currentConfig !== undefined) {
            setDateRange(mappingDateRange(currentConfig, type));
        }

        //https://stackoverflow.com/questions/55840294/how-to-fix-missing-dependency-warning-when-using-useeffect-react-hook
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type]);

    // Handle utilisé lors du changement du type de période
    const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
        setType(newValue);
    };

    // Handle utilisé lors du changement de périodes
    const handleChangeDate = (
        _dates: DateObject[],
        options: { validatedValue: string | string[]; input: HTMLElement; isTyping: boolean }
    ) => {
        if (options.validatedValue !== undefined && options.validatedValue.length > 1 && undefined !== dateRange) {
            const updateDates = mapDatePickerToDateApi(options.validatedValue, type);

            const updateDateRange: DateRangeDto = {
                ...dateRange,
                start: updateDates[0],
                end: updateDates[1],
            };
            setDateRange(updateDateRange);
        }
    };

    useEffect(() => {
        // A chaque change de l'objet date range on recharge les données
        getConsommations();

        //https://stackoverflow.com/questions/55840294/how-to-fix-missing-dependency-warning-when-using-useeffect-react-hook
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange]);

    // Fonction pour appeler le service de récupération des données.
    const getConsommations = () => {
        if (dateRange !== undefined) {
            setIsSearching(true);
            getConsommationsTeleverse(
                props.contractId,
                props.config.numeroCompteur,
                type,
                dateRange.start,
                dateRange.end
            )
                .then(result => {
                    // On map les données pour le graph
                    setDatas(result);
                    setDataY(result.map(conso => Number(conso.valeur)));
                    setDataX(result.map(conso => conso.libelle));
                })
                .catch(error => {
                    let data: Array<ConsommationTelereleveDto> = [];
                    if (type === 'years') {
                        for (let year = parseInt(dateRange.start); year <= parseInt(dateRange.end); year++) {
                            data.push({ libelle: year.toString(), information: 'Absence de données', valeur: 0.0 });
                        }
                        setDatas(data);
                        setDataY(data.map(conso => Number(conso.valeur)));
                        setDataX(data.map(conso => conso.libelle));
                    }

                    if (type === 'months') {
                        const [startYear, startMonth] = dateRange.start.split('-').map(Number);
                        const [endYear, endMonth] = dateRange.end.split('-').map(Number);

                        let currentDate = new Date(startYear, startMonth - 1, 1);
                        const endDate = new Date(endYear, endMonth - 1, 1);

                        while (currentDate <= endDate) {
                            const month = currentDate.getMonth() + 1;
                            const year = currentDate.getFullYear();
                            const formattedMonth = month.toString().padStart(2, '0');
                            currentDate.setMonth(currentDate.getMonth() + 1);
                            data.push({
                                libelle: `${formattedMonth}/${year}`,
                                information: 'Absence de données',
                                valeur: 0.0,
                            });
                        }
                        setDatas(data);
                        setDataY(data.map(conso => Number(conso.valeur)));
                        setDataX(data.map(conso => conso.libelle));
                    }

                    if (type === 'days') {
                        let currentDate = new Date(dateRange.start);
                        const endDate = new Date(dateRange.end);

                        currentDate.setHours(4, 0, 0, 0);
                        endDate.setHours(4, 0, 0, 0);

                        while (currentDate <= endDate) {
                            data.push({
                                libelle: `${currentDate.toLocaleDateString()}`,
                                information: 'Absence de données',
                                valeur: 0.0,
                            });
                            currentDate.setDate(currentDate.getDate() + 1);
                            setDatas(data);
                            setDataY(data.map(conso => Number(conso.valeur)));
                            setDataX(data.map(conso => conso.libelle));
                        }
                    }
                    showAxiosMessageError(error, showMessage);
                })
                .finally(() => {
                    setIsSearching(false);
                });
        }
    };

    return (
        <Stack spacing={2} sx={{ alignItems: 'center' }}>
            <Tabs sx={{ width: '100%' }} value={type} onChange={handleChange} aria-label="Durée" centered>
                {props.config &&
                    props.config.configurationGraphique.map(typeConfig => (
                        <Tab
                            key={typeConfig.type}
                            value={typeConfig.type}
                            label={mappingTypConso(typeConfig.type)}
                            wrapped
                        />
                    ))}
            </Tabs>
            <Tooltip title="Sélectionnez une date de début et de fin" placement="right">
                <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                    sx={{
                        justifyContent: 'center',
                        marginTop: '0 !important',
                        padding: 1,
                    }}
                >
                    {dateRange && (
                        <>
                            <DatePicker
                                id={'dataPicker'}
                                value={dateRange.values}
                                format={dateRange.formatPeriod}
                                onlyYearPicker={dateRange.showOnlyYears}
                                onlyMonthPicker={dateRange.showOnlyMonths}
                                onChange={handleChangeDate}
                                range
                                minDate={dateRange.minStart}
                                maxDate={dateRange.maxEnd}
                                dateSeparator={' - '}
                                style={{
                                    border: 'none',
                                    textAlign: 'center',
                                    color: theme.palette.skyBlue.main,
                                    cursor: 'pointer',
                                }}
                                locale={gregorianFr}
                            />
                            <ArrowDropDownIcon className={'inputIcon'} sx={{ color: theme.palette.skyBlue.main }} />
                        </>
                    )}
                </Box>
            </Tooltip>
            <Divider
                variant="middle"
                sx={{ width: '20%', marginTop: '0 !important', backgroundColor: theme.palette.skyBlue.main }}
            />
            {isSearching ? (
                <Skeleton variant="rectangular" height={150} width={'100%'}></Skeleton>
            ) : (
                datas && (
                    <BarChart
                        className={'chart'}
                        xAxis={[
                            {
                                data: dataX,
                                scaleType: 'band',
                                valueFormatter: (value: string, context: AxisValueFormatterContext) =>
                                    context.location === 'tick'
                                        ? value
                                        : `${value} : ${datas?.find(d => d.libelle === value)?.information}`,
                            },
                        ]}
                        series={[
                            {
                                data: dataY,
                                valueFormatter: value => `Volume : ${value} m³`,
                            },
                        ]}
                        width={500}
                        height={300}
                    />
                )
            )}
        </Stack>
    );
}

/**
 * Function for mapping date picker date to api date by type date
 * @param datePickers dates
 * @param type type of date
 */
function mapDatePickerToDateApi(datePickers: string | string[], type: string): string[] {
    let startDate = '';
    let endDate = '';
    if (type === YEARS) {
        startDate = datePickers[0];
        endDate = datePickers[1];
    } else if (type === MONTHS) {
        const [startMonth, startYear] = datePickers[0].split('/');
        const [endMonth, endYear] = datePickers[1].split('/');
        startDate = startYear + '-' + startMonth;
        endDate = endYear + '-' + endMonth;
    } else if (type === DAYS) {
        const [startDay, startMonth, startYear] = datePickers[0].split('/');
        const [endDay, endMonth, endYear] = datePickers[1].split('/');
        startDate = startYear + '-' + startMonth + '-' + startDay;
        endDate = endYear + '-' + endMonth + '-' + endDay;
    }

    return [startDate, endDate];
}

/**
 * Function for initialize values for date picker.
 * @param configGraph config for graph
 * @param type type of date
 */
function mappingDateRange(configGraph: ConfigGraphConsommationsDto, type: string): DateRangeDto {
    // Mise en place des valeurs par defaut
    let start = configGraph.start;
    let end = configGraph.end;
    let minStart = configGraph.start;
    let maxEnd = configGraph.end;
    let formatPeriod = '';
    let values: Date[] = [];
    let showOnlyYears = false;
    let showOnlyMonths = false;
    switch (type) {
        //Changement des valeurs pour le type année
        case YEARS:
            formatPeriod = 'YYYY';
            values = [new Date(Number(start), 0, 1), new Date(Number(end), 0, 1)];
            showOnlyYears = true;
            break;
        //Changement des valeurs pour le type mois
        case MONTHS:
            formatPeriod = 'MM/YYYY';
            // On est obligé de split les date pour remettre au bon format
            const [startYear, startMonth] = start.split('-');
            const [endYear, endMonth] = end.split('-');
            minStart = startMonth + '/' + startYear;
            maxEnd = endMonth + '/' + endYear;
            //On change la date de début pour faire une période d'un an
            const startDate = new Date(Number(endYear), Number(endMonth) - 1, 1);
            startDate.setFullYear(startDate.getFullYear() - 1);
            start = startDate.getFullYear() + '-' + (startDate.getMonth() + 1);
            values = [startDate, new Date(Number(endYear), Number(endMonth) - 1, 1)];
            showOnlyMonths = true;
            break;
        // Changement des valeurs pour le type jours
        case DAYS:
            formatPeriod = 'DD/MM/YYYY';
            const startDateDays = new Date(end);
            // Obligé de split les dates car pas au même format
            const [startYearDays, startMonthDays, startDayDays] = start.split('-');
            const [endYearDays, endMonthDays, endDayDays] = end.split('-');
            minStart = startDayDays + '/' + startMonthDays + '/' + startYearDays;
            maxEnd = endDayDays + '/' + endMonthDays + '/' + endYearDays;
            // On recalcul la date de début pour avoir une période sur un mois
            startDateDays.setMonth(startDateDays.getMonth() - 1);
            values = [startDateDays, new Date(end)];
            start = startDateDays.getFullYear() + '-' + (startDateDays.getMonth() + 1) + '-' + startDateDays.getDate();
            break;
        default:
            break;
    }
    return { start, end, minStart, maxEnd, formatPeriod, values, showOnlyYears, showOnlyMonths };
}
